import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

/**
 * UserMenu
 *
 * Displays a notifications bell icon and user avatar with a dropdown menu.
 * Reads user info from localStorage (set during Google login).
 *
 * Menu items: Profile, Preferences, Log Out
 * (Only Log Out is functional for now)
 */
export default function UserMenu({ onPreferencesClick }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  // Read user info from localStorage, with JWT fallback
  let userName = localStorage.getItem("userName") || "";
  let userPicture = localStorage.getItem("userPicture") || "";

  // Fallback: try to extract from JWT token if not in localStorage
  if (!userName) {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        userName = payload.name || payload.email?.split("@")[0] || "User";
        if (payload.picture) userPicture = payload.picture;
        // Cache for next time
        localStorage.setItem("userName", userName);
        if (userPicture) localStorage.setItem("userPicture", userPicture);
      } catch (e) { /* ignore */ }
    }
  }
  if (!userName) userName = "User";

  const firstName = userName.split(" ")[0];

  // Close menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    localStorage.setItem("token", "guest_token");
    localStorage.setItem("userEmail", "guest@alffy.com");
    localStorage.setItem("userName", "Guest");
    localStorage.setItem("isGuest", "true");
    localStorage.removeItem("userPicture");
    setIsMenuOpen(false);
    navigate("/stockview");
    window.location.reload();
  };

  const handleGoogleSignIn = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
    if (!clientId) {
      navigate("/login");
      return;
    }
    const redirectUri = encodeURIComponent(window.location.origin + "/login");
    const nonce = Math.random().toString(36).substring(2, 18);
    window.location.href =
      `https://accounts.google.com/o/oauth2/v2/auth` +
      `?client_id=${clientId}` +
      `&redirect_uri=${redirectUri}` +
      `&response_type=id_token` +
      `&scope=openid%20email%20profile` +
      `&nonce=${nonce}` +
      `&prompt=select_account`;
  };

  const menuItems = [
    {
      label: "Profile",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
        </svg>
      ),
      onClick: () => setIsMenuOpen(false),
    },
    {
      label: "Preferences",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      onClick: () => { setIsMenuOpen(false); onPreferencesClick && onPreferencesClick(); },
    },
    {
      label: "Log Out",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
        </svg>
      ),
      onClick: handleLogout,
      danger: true,
    },
  ];

  if (localStorage.getItem("isGuest") === "true") {
    return (
      <div className="flex items-center gap-4 mr-2">
        <button
          onClick={handleGoogleSignIn}
          className="text-gray-300 hover:text-white font-semibold text-sm transition-colors focus:outline-none"
        >
          Login
        </button>
        <button
          onClick={handleGoogleSignIn}
          className="py-2 px-4 bg-blue-600 hover:bg-blue-500 active:scale-[0.97] text-white font-bold text-sm rounded-xl shadow-lg shadow-blue-600/20 hover:shadow-blue-500/30 transition-all focus:outline-none"
        >
          Sign Up for Free
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {/* Notifications Bell */}
      <button
        className="relative p-1.5 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-gray-700/50 transition-colors"
        title="Notifications"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
      </button>

      {/* Divider */}
      <div className="w-px h-5 bg-gray-700"></div>

      {/* User Avatar + Name + Dropdown */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setIsMenuOpen((prev) => !prev)}
          className="flex items-center gap-2 px-1.5 py-1 rounded-lg hover:bg-gray-700/50 transition-colors"
        >
          {/* Welcome text */}
          <span className="text-sm text-gray-400">
            Welcome, <span className="text-gray-200 font-medium">{firstName}</span>
          </span>

          {/* Avatar */}
          {userPicture ? (
            <img
              src={userPicture}
              alt={firstName}
              className="w-7 h-7 rounded-full border border-gray-600 hover:border-blue-500 transition-colors object-cover"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center text-white text-xs font-bold border border-gray-600">
              {firstName.charAt(0).toUpperCase()}
            </div>
          )}

          {/* Chevron */}
          <svg
            className={`w-3 h-3 text-gray-500 transition-transform ${isMenuOpen ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown Menu */}
        {isMenuOpen && (
          <div className="absolute right-0 mt-2 w-48 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-150">
            {/* User info header */}
            <div className="px-4 py-3 border-b border-gray-700 bg-gray-800/80">
              <p className="text-sm font-medium text-gray-200 truncate">{userName}</p>
              <p className="text-xs text-gray-500 truncate">
                {localStorage.getItem("userEmail") || ""}
              </p>
            </div>

            {/* Menu items */}
            <div className="py-1">
              {menuItems.map((item) => (
                <button
                  key={item.label}
                  onClick={item.onClick}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                    item.danger
                      ? "text-red-400 hover:bg-red-500/10 hover:text-red-300"
                      : "text-gray-300 hover:bg-gray-700/50 hover:text-gray-100"
                  }`}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
